import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_sriman-enterprise/artifacts/ng31c679_image.png";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const from = location.state?.from || "/admin";

  useEffect(() => {
    if (user && user.role === "admin") {
      navigate(from, { replace: true });
    }
  }, [user, from, navigate]);

  const inputClass =
    "w-full bg-[#0B132B]/60 border border-white/10 focus:border-red-500/60 focus:outline-none text-white placeholder-slate-500 px-4 py-3.5 text-sm rounded-sm transition-colors";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Enter email and password.");
      return;
    }
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Welcome back, ${u.name}`);
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Login failed",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      data-testid="admin-login-page"
      className="min-h-screen bg-[#040812] flex items-center justify-center px-6 py-24 relative overflow-hidden"
    >
      <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
      <div className="absolute -top-40 -right-40 w-96 h-96 bg-red-500/10 blur-3xl rounded-full pointer-events-none" />

      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 mb-10">
          <span className="h-10 w-10">
            <img
              src={LOGO_URL}
              alt="Sriman Solutions"
              className="h-full w-full object-contain"
            />
          </span>
          <span className="font-display text-white text-base tracking-tight">
            Sriman Solutions
          </span>
        </Link>

        <div className="bg-[#0B132B]/50 border border-white/10 p-8 md:p-10">
          <Lock size={20} className="text-red-500" strokeWidth={1.5} />
          <span className="overline mt-6 block">Admin console</span>
          <h1 className="mt-3 font-display text-white text-3xl md:text-4xl tracking-tighter font-light leading-tight">
            Sign in to review
            <br />
            client inquiries.
          </h1>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4">
            <div>
              <label className="overline mb-2 block">Email</label>
              <input
                data-testid="admin-login-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
                placeholder="admin@srimansolutions.com"
                required
              />
            </div>
            <div>
              <label className="overline mb-2 block">Password</label>
              <input
                data-testid="admin-login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputClass}
                placeholder="••••••••"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="group w-full inline-flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 disabled:opacity-70 text-white font-medium px-6 py-3.5 rounded-sm transition-colors mt-2"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Signing in…
                </>
              ) : (
                <>
                  Sign in
                  <ArrowRight
                    size={16}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </>
              )}
            </button>
          </form>

          <div className="mt-8 text-xs text-slate-500 font-mono tracking-wider">
            Access is logged. Authorized personnel only.
          </div>
        </div>

        <Link
          to="/"
          className="mt-8 inline-block text-slate-500 text-xs hover:text-white transition-colors"
        >
          ← Back to site
        </Link>
      </div>
    </main>
  );
}
